import { SpreadsheetTableCellSelection, SpreadsheetTableSelect, SpreadsheetTableSelectionState } from "../table.types";

/**
 * Creates a selection from start to end cell (supports rectangular selection)
 */
export const createSelection = (
  start: SpreadsheetTableCellSelection,
  end: SpreadsheetTableCellSelection
): SpreadsheetTableSelect => {
  return { start, end };
};

/**
 * Checks if a cell is within the current selection
 */
export const isCellInSelection = (
  rowIndex: number,
  columnId: number | number,
  selection: SpreadsheetTableSelect | null,
  columnIds: number[]
): boolean => {
  if (!selection) return false;

  const { start, end } = selection;

  // Determine the row range
  const minRow = Math.min(start.rowIndex, end.rowIndex);
  const maxRow = Math.max(start.rowIndex, end.rowIndex);

  // Determine the column range
  const startColIndex = columnIds.indexOf(start.columnId);
  const endColIndex = columnIds.indexOf(end.columnId);
  const minColIndex = Math.min(startColIndex, endColIndex);
  const maxColIndex = Math.max(startColIndex, endColIndex);

  const currentColIndex = columnIds.indexOf(columnId);

  // Check if the cell is within the rectangular selection
  const isRowInRange = rowIndex >= minRow && rowIndex <= maxRow;
  const isColumnInRange =
    currentColIndex >= minColIndex && currentColIndex <= maxColIndex;

  return isRowInRange && isColumnInRange;
};

/**
 * Gets all selected cells from a selection
 */
export const getSelectedCells = (selection: SpreadsheetTableSelect, columnIds: number[]): SpreadsheetTableCellSelection[] => {
  const { start, end } = selection;
  const cells: SpreadsheetTableCellSelection[] = [];
  
  const minRow = Math.min(start.rowIndex, end.rowIndex);
  const maxRow = Math.max(start.rowIndex, end.rowIndex);
  
  const startColIndex = columnIds.indexOf(start.columnId);
  const endColIndex = columnIds.indexOf(end.columnId);
  const minColIndex = Math.min(startColIndex, endColIndex);
  const maxColIndex = Math.max(startColIndex, endColIndex);
  
  // Generate all cells in the rectangular selection
  for (let rowIndex = minRow; rowIndex <= maxRow; rowIndex++) {
    for (let colIndex = minColIndex; colIndex <= maxColIndex; colIndex++) {
      const columnId = columnIds[colIndex];
      if (columnId !== undefined) {
        cells.push({
          rowIndex,
          columnId,
        });
      }
    }
  }
  
  return cells;
};

/**
 * Creates a new selection state
 */
export const createSelectionState = (): SpreadsheetTableSelectionState => {
  return {
    selection: null,
    isSelecting: false
  };
};

/**
 * Updates selection state with a new selection
 */
export const updateSelection = (
  currentState: SpreadsheetTableSelectionState,
  newSelection: SpreadsheetTableSelect | null
): SpreadsheetTableSelectionState => {
  return {
    ...currentState,
    selection: newSelection,
    isSelecting: false
  };
};

/**
 * Starts selection mode
 */
export const startSelection = (
  currentState: SpreadsheetTableSelectionState
): SpreadsheetTableSelectionState => {
  return {
    ...currentState,
    isSelecting: true
  };
};

/**
 * Determines the border position for a cell in a selection
 * Returns an object with border positions (top, right, bottom, left) that should be shown
 */
export const getCellBorderPosition = (
  rowIndex: number,
  columnId: number,
  selection: SpreadsheetTableSelect | null,
  columnIds: number[]
): { top: boolean; right: boolean; bottom: boolean; left: boolean } => {
  if (!selection) return { top: false, right: false, bottom: false, left: false };
  
  const { start, end } = selection;
  
  // Determine the row range
  const minRow = Math.min(start.rowIndex, end.rowIndex);
  const maxRow = Math.max(start.rowIndex, end.rowIndex);
  
  // Determine the column range
  const startColIndex = columnIds.indexOf(start.columnId);
  const endColIndex = columnIds.indexOf(end.columnId);
  const minColIndex = Math.min(startColIndex, endColIndex);
  const maxColIndex = Math.max(startColIndex, endColIndex);
  
  const currentColIndex = columnIds.indexOf(columnId);
  
  // Check if the cell is within the rectangular selection
  const isRowInRange = rowIndex >= minRow && rowIndex <= maxRow;
  const isColumnInRange = currentColIndex >= minColIndex && currentColIndex <= maxColIndex;
  
  if (!isRowInRange || !isColumnInRange) {
    return { top: false, right: false, bottom: false, left: false };
  }
  
  // Determine which borders to show
  return {
    top: rowIndex === minRow,
    right: currentColIndex === maxColIndex,
    bottom: rowIndex === maxRow,
    left: currentColIndex === minColIndex
  };
};

/**
 * Creates a row selection spanning all columns for a given row
 * Used when clicking on the serial number column to select the entire row
 */
export const createRowSelection = (
  rowIndex: number,
  columnIds: number[]
): SpreadsheetTableSelect => {
  if (columnIds.length === 0) {
    throw new Error('Column IDs array cannot be empty');
  }
  
  const firstColumnId = columnIds[0]!;
  const lastColumnId = columnIds[columnIds.length - 1]!;
  
  return {
    start: { rowIndex, columnId: firstColumnId },
    end: { rowIndex, columnId: lastColumnId }
  };
};

/**
 * Creates a row range selection spanning all columns for multiple rows
 * Used for shift+click row selection to select a range of rows
 */
export const createRowRangeSelection = (
  startRowIndex: number,
  endRowIndex: number,
  columnIds: number[]
): SpreadsheetTableSelect => {
  if (columnIds.length === 0) {
    throw new Error('Column IDs array cannot be empty');
  }
  
  const firstColumnId = columnIds[0]!;
  const lastColumnId = columnIds[columnIds.length - 1]!;
  
  // Ensure startRowIndex is the smaller value
  const actualStartRow = Math.min(startRowIndex, endRowIndex);
  const actualEndRow = Math.max(startRowIndex, endRowIndex);
  
  return {
    start: { rowIndex: actualStartRow, columnId: firstColumnId },
    end: { rowIndex: actualEndRow, columnId: lastColumnId }
  };
};

// =============================================================================
// ROW SELECTION UTILITIES
// =============================================================================

/**
 * Row selection state type
 */
export type RowSelectionState = {
  selectedRows: Set<number>;
  lastSelectedRowIndex: number | null;
  isRowSelectionMode: boolean;
};

/**
 * Creates a new row selection state
 */
export const createRowSelectionState = (): RowSelectionState => {
  return {
    selectedRows: new Set(),
    lastSelectedRowIndex: null,
    isRowSelectionMode: false
  };
};

/**
 * Checks if a row is selected in the row selection state
 */
export const isRowSelected = (
  rowIndex: number,
  rowSelectionState: RowSelectionState
): boolean => {
  return rowSelectionState.selectedRows.has(rowIndex);
};

/**
 * Handles single row selection (clears other selections)
 */
export const selectSingleRow = (
  currentState: RowSelectionState,
  rowIndex: number
): RowSelectionState => {
  return {
    ...currentState,
    selectedRows: new Set([rowIndex]),
    lastSelectedRowIndex: rowIndex,
    isRowSelectionMode: true
  };
};

/**
 * Handles ctrl+click row selection (toggle row in/out of selection)
 */
export const toggleRowSelection = (
  currentState: RowSelectionState,
  rowIndex: number
): RowSelectionState => {
  const newSelectedRows = new Set(currentState.selectedRows);
  
  if (newSelectedRows.has(rowIndex)) {
    newSelectedRows.delete(rowIndex);
  } else {
    newSelectedRows.add(rowIndex);
  }
  
  return {
    ...currentState,
    selectedRows: newSelectedRows,
    lastSelectedRowIndex: newSelectedRows.has(rowIndex) ? rowIndex : currentState.lastSelectedRowIndex,
    isRowSelectionMode: true
  };
};

/**
 * Handles shift+click row selection (select range from last selected to current)
 */
export const selectRowRange = (
  currentState: RowSelectionState,
  rowIndex: number
): RowSelectionState => {
  if (currentState.lastSelectedRowIndex === null) {
    // If no last selected row, just select the current row
    return selectSingleRow(currentState, rowIndex);
  }
  
  const startRow = Math.min(currentState.lastSelectedRowIndex, rowIndex);
  const endRow = Math.max(currentState.lastSelectedRowIndex, rowIndex);
  const newSelectedRows = new Set<number>();
  
  for (let i = startRow; i <= endRow; i++) {
    newSelectedRows.add(i);
  }
  
  return {
    ...currentState,
    selectedRows: newSelectedRows,
    lastSelectedRowIndex: rowIndex,
    isRowSelectionMode: true
  };
};

/**
 * Clears all row selections
 */
export const clearRowSelection = (
  currentState: RowSelectionState
): RowSelectionState => {
  return {
    ...currentState,
    selectedRows: new Set(),
    lastSelectedRowIndex: null,
    isRowSelectionMode: false
  };
};

/**
 * Gets all selected row indices as an array
 */
export const getSelectedRowIndices = (
  rowSelectionState: RowSelectionState
): number[] => {
  return Array.from(rowSelectionState.selectedRows).sort((a, b) => a - b);
};